import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ShoppingBag } from 'lucide-react';
import { formatNumber } from '@/lib/api';
import { productImage } from '@/lib/images';
import { Picture } from './Picture';
import { cn } from '@/lib/utils';

export function PriceTag({ product, className, size = 'md' }) {
  if (product.priceType === 'quote' || product.pricePerTonne === null || product.pricePerTonne === undefined) {
    return (
      <span className={cn('inline-flex items-center gap-1.5 font-medium text-[var(--hr-heading)]', size === 'lg' ? 'text-[22px]' : 'text-[15px]', className)} data-testid={`price-${product.slug}`}>
        Prosjektpris
      </span>
    );
  }
  return (
    <span className={cn('hr-tabular inline-flex items-baseline gap-1 text-[var(--hr-heading)]', className)} data-testid={`price-${product.slug}`}>
      <span className={cn('font-semibold', size === 'lg' ? 'text-[32px] leading-none' : 'text-[17px]')}>{formatNumber(product.pricePerTonne)}</span>
      <span className={cn('text-[var(--hr-muted)]', size === 'lg' ? 'text-[14px]' : 'text-[12px]')}>kr/tonn eks. mva</span>
    </span>
  );
}

/** Produktkort for katalog og forside. */
export function ProductCard({ product, className, compact = false }) {
  const isQuote = product.priceType === 'quote';
  return (
    <Link
      to={`/produkter/${product.slug}`}
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-[var(--hr-radius-lg)] border border-[var(--hr-hairline)] bg-white shadow-[var(--hr-shadow-xs)] transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:border-[rgba(31,33,48,0.18)] hover:shadow-[var(--hr-shadow-md)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)] focus-visible:ring-offset-2',
        className,
      )}
      data-testid={`product-card-${product.slug}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-[var(--hr-inset)]">
        <Picture variant={productImage(product.image)} sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw" alt={product.name} className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]" />
        <span className="absolute left-3 top-3 inline-flex h-7 items-center rounded-full bg-white/90 px-2.5 text-[12px] font-medium text-[var(--hr-heading)] shadow-[var(--hr-shadow-xs)] backdrop-blur">{product.fraction}</span>
        {isQuote && <span className="absolute right-3 top-3 inline-flex h-7 items-center rounded-full bg-[var(--hr-navy)] px-2.5 text-[12px] font-medium text-white">Pris etter avtale</span>}
      </div>
      <div className={cn('flex flex-1 flex-col', compact ? 'p-4' : 'p-5')}>
        <h3 className="font-display text-[18px] font-semibold leading-tight tracking-[-0.01em] text-[var(--hr-heading)]">{product.name}</h3>
        {!compact && <p className="mt-1.5 line-clamp-2 text-[14px] leading-6 text-[var(--hr-muted)]">{product.short}</p>}
        <div className="mt-auto flex items-end justify-between gap-3 pt-4">
          <PriceTag product={product} />
          <span className={cn('inline-flex h-9 items-center gap-1.5 rounded-[10px] px-3 text-[13px] font-semibold transition-colors duration-150', isQuote ? 'border border-[var(--hr-border)] text-[var(--hr-heading)] group-hover:border-[var(--site-ink)]' : 'bg-[var(--site-ink)] text-white group-hover:bg-[var(--hr-navy)]')}>
            {isQuote ? (
              <>
                Forespør <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </>
            ) : (
              <>
                <ShoppingBag className="h-4 w-4" aria-hidden="true" /> Bestill
              </>
            )}
          </span>
        </div>
      </div>
    </Link>
  );
}
